import type { ReactNode } from 'react';
import type { DsTone } from 'src/auditoria/components/ds';

import { useRef } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import InputBase from '@mui/material/InputBase';

import {
  DsButton,
  mapStatus,
  DsCellLink,
  DsFilterPill,
  DsIconButton,
  DS_BOX_RADIUS,
} from 'src/auditoria/components/ds';

// ----------------------------------------------------------------------
// Peças de conversa e de formulário compartilhadas por `portal-protocolo` e
// `portal-plantao-fiscal`: os dois artboards têm a mesma composição —
// grade + painel de thread à esquerda, painel de 300px à direita.
// ----------------------------------------------------------------------

/** Faixa da tela: coluna elástica à esquerda, painel de 300px à direita. */
export const SX_COLUNAS = {
  display: 'flex',
  gap: '16px',
  alignItems: 'flex-start',
  flexWrap: { xs: 'wrap', md: 'nowrap' },
} as const;

/**
 * Tom do chip de situação segundo os artboards do portal: `Em análise` em
 * warning, `Respondido` em success, `Concluído` em info. Não bate com o
 * `mapStatus` do kit (que dá info / default / success para os mesmos textos),
 * então o artboard decide o que ele declara e o `mapStatus` cobre o resto.
 * Divergência registrada no mailbox do lote.
 */
export function toneSituacao(status: string): DsTone {
  const chave = status.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

  if (chave.includes('analise')) return 'warning';
  if (chave.includes('respond')) return 'success';
  if (chave.includes('conclu')) return 'info';

  return mapStatus(status);
}

// ----------------------------------------------------------------------
// Conversa
// ----------------------------------------------------------------------

export type BalaoMensagem = {
  id: number | string;
  texto: string;
  /** `true` = balão `.msg.out` (do contribuinte); `false` = `.msg.in` (do fisco). */
  doContribuinte: boolean;
};

/** `.msg` / `.msg.in` / `.msg.out` do artboard. */
function Balao({ msg }: { msg: BalaoMensagem }) {
  return (
    <Box
      sx={[
        {
          maxWidth: '78%',
          padding: '10px 14px',
          borderRadius: '12px',
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        },
        msg.doContribuinte
          ? {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderTopRightRadius: '4px',
              ml: 'auto',
            }
          : {
              bgcolor: 'background.neutral',
              color: 'text.primary',
              borderTopLeftRadius: '4px',
            },
      ]}
    >
      {msg.texto}
    </Box>
  );
}

/** `.fld` do portal: 12px/600, borda de 1px, raio 8, sobre `background.default`. */
const SX_FLD = {
  flex: 1,
  minWidth: 0,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${DS_BOX_RADIUS}px`,
  padding: '9px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: 'text.primary',
  bgcolor: 'background.default',
  '& input::placeholder': {
    fontSize: 12,
    fontWeight: 500,
    color: 'text.secondary',
    opacity: 1,
  },
};

export type PainelConversaProps = {
  /** `Protocolo 2025-00412 · Revisão de IPTU` — 13px/800 no artboard. */
  titulo: ReactNode;
  mensagens: BalaoMensagem[];
  vazio: string;
  placeholder: string;
  valor: string;
  onChange: (v: string) => void;
  onEnviar: () => void;
  enviando?: boolean;
  desabilitado?: boolean;
};

export function PainelConversa({
  titulo,
  mensagens,
  vazio,
  placeholder,
  valor,
  onChange,
  onEnviar,
  enviando = false,
  desabilitado = false,
}: PainelConversaProps) {
  return (
    <Card sx={{ padding: '16px 18px', mt: '12px' }}>
      <Box sx={{ fontSize: 13, fontWeight: 800, mb: '8px' }}>{titulo}</Box>

      {mensagens.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mensagens.map((m) => (
            <Balao key={m.id} msg={m} />
          ))}
        </Box>
      ) : (
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{vazio}</Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '10px' }}>
        <InputBase
          value={valor}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={desabilitado || enviando}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onEnviar();
            }
          }}
          sx={SX_FLD}
        />

        {/* O canvas não desenha botão aqui; o `.iconbtn` de 30px é o único
            botão-ícone que ele admite, e sem ele a única forma de enviar seria
            a tecla Enter. Registrado no mailbox. */}
        <DsIconButton
          icon="enviar"
          aria-label="Enviar mensagem"
          onClick={onEnviar}
          disabled={desabilitado || enviando || !valor.trim()}
        />
      </Box>
    </Card>
  );
}

// ----------------------------------------------------------------------
// Anexos — `.btnsec` + pílulas removíveis, tudo do kit
// ----------------------------------------------------------------------

export type AnexosCanvasProps = {
  arquivos: File[];
  onChange: (arquivos: File[]) => void;
  disabled?: boolean;
};

/**
 * Substitui o `AnexosInput` compartilhado dentro das telas do canvas: aquele
 * usa `Chip` do MUI e ícone `eva:attach-2-fill`, e o kit proíbe ícone sólido
 * novo. Aqui a pílula é `DsFilterPill` e o ícone é o `anexo` de traço.
 */
export function AnexosCanvas({ arquivos, onChange, disabled = false }: AnexosCanvasProps) {
  const entrada = useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {arquivos.map((a, i) => (
        <DsFilterPill
          key={`${a.name}-${i}`}
          onDelete={disabled ? undefined : () => onChange(arquivos.filter((_, j) => j !== i))}
        >
          {a.name}
        </DsFilterPill>
      ))}

      <DsButton
        tone="neutral"
        icon="anexo"
        disabled={disabled}
        onClick={() => entrada.current?.click()}
      >
        Anexar arquivos
      </DsButton>

      <Box
        component="input"
        type="file"
        multiple
        hidden
        ref={entrada}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChange([...arquivos, ...Array.from(e.target.files ?? [])]);
          e.target.value = '';
        }}
      />
    </Box>
  );
}

// ----------------------------------------------------------------------

/** Título do painel de conversa com o número clicável para o processo completo. */
export function TituloConversa({
  numero,
  assunto,
  aoAbrir,
}: {
  numero: string;
  assunto: string | null;
  aoAbrir: () => void;
}) {
  return (
    <Box component="span">
      <DsCellLink mono onClick={aoAbrir}>
        {numero}
      </DsCellLink>
      {assunto ? ` · ${assunto}` : ''}
    </Box>
  );
}
